import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { Link, useNavigate } from '@faceup/router'
import { ArrowGoBack } from '@faceup/ui'
import { Button, Col, Modal, Row, Skeleton } from '@faceup/ui-base'
import { ChannelType } from '@faceup/utils'
import { useContext, useState } from 'react'
import PageTitle from '../../Components/PageTitle'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { UserContext } from '../../Contexts/UserContext'
import { useSavedReportsContext } from '../../hooks/useSavedReportsContext'
import { sizeSettings } from '../../utils/constants'
import CheckPinForm from './CheckPinForm'
import { useCheckReportLogin } from './useCheckReportLogin'

// All channels resolved to identical copy, so the per-channel maps were
// collapsed to single messages during the focalazy migration. Re-introduce a
// per-channel split via `defineMessages('ns', Object.values(ChannelType)...)`
// if channel-specific copy is ever needed.
const messages = defineMessages('followUp.pages.checkReport', [
  { id: 'forgottenPin', defaultMessage: '' },
  { id: 'forgottenPinTitle', defaultMessage: '' },
  { id: 'forgottenPinDescription', defaultMessage: '', wrappers: { i: true } },
  { id: 'title', defaultMessage: '' },
  { id: 'description', defaultMessage: '' },
  { id: 'pinLabel', defaultMessage: '' },
  { id: 'pinError', defaultMessage: '' },
  { id: 'pinHint', defaultMessage: '' },
] as const)

const CheckReport = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { hasSomeSavedReports } = useSavedReportsContext()
  const { client } = useContext(LayoutContext)
  const [forgottenPinModalOpen, setForgottenPinModalOpen] = useState(false)
  const { reportSource } = useContext(UserContext)

  const { pinInput, setPinInput, loading, onSubmit } = useCheckReportLogin({
    onSuccess: () => navigate(routes => routes.report()),
  })

  const isApp = client === 'app'

  if (!reportSource) {
    return <Skeleton />
  }

  const channelType = reportSource.channelType
  if (channelType === ChannelType.Survey) {
    throw new Error('CheckReport should not be rendered for surveys')
  }

  return (
    <>
      <Row justify='center'>
        <Col {...sizeSettings}>
          {isApp && hasSomeSavedReports && (
            <div className='absolute'>
              <Link to={routes => routes.checkReport()}>
                <ArrowGoBack />
              </Link>
            </div>
          )}
          <PageTitle
            className='mb-10'
            description={<FormattedMessage message={messages.description} />}
          >
            <FormattedMessage message={messages.title} />
          </PageTitle>
        </Col>
      </Row>
      <Row justify='center'>
        <Col {...sizeSettings}>
          <CheckPinForm
            pinInput={pinInput}
            setPinInput={setPinInput}
            loading={loading}
            onSubmit={onSubmit}
            label={<FormattedMessage message={messages.pinLabel} />}
            hint={formatMessage(messages.pinHint)}
            errorMessage={<FormattedMessage message={messages.pinError} />}
          />
          <div className='flex justify-center'>
            <Button type='link' onClick={() => setForgottenPinModalOpen(true)}>
              <FormattedMessage message={messages.forgottenPin} />
            </Button>
          </div>
        </Col>
        <Modal
          open={forgottenPinModalOpen}
          title={<FormattedMessage message={messages.forgottenPinTitle} />}
          onClose={() => setForgottenPinModalOpen(false)}
        >
          <FormattedMessage
            message={messages.forgottenPinDescription}
            wrappers={{
              i: text => <i>{text}</i>,
            }}
          />
        </Modal>
      </Row>
    </>
  )
}

export default CheckReport
